import { ReactNode, useState } from 'react';
import { Grid, Button } from '@mui/material';
import {
  SaveButton,
  Toolbar,
  ToolbarProps,
  DeleteWithConfirmButton,
  useRecordContext,
  useResourceContext,
} from 'react-admin';
import { Cancel as CancelIcon, Delete as DeleteIcon } from '@mui/icons-material';

import BackButton from './BackButton';
import ConfirmDialog from './ConfirmDialog';

interface DeleteOptions {
  showDeleteButton?: boolean;
  dialogTitle?: string | undefined;
  dialogContent?: ReactNode;
  onConfirmDelete?: (() => void) | undefined;
}

interface FormToolbarProps extends ToolbarProps {
  disableSave?: boolean;
  deleteOptions?: DeleteOptions;
}

const FormToolbar = (props: FormToolbarProps) => {
  const record = useRecordContext();
  const resource = useResourceContext();

  const { deleteOptions, disableSave, ...baseProps } = props;

  const {
    showDeleteButton = true,
    dialogTitle: confirmDeleteDialogTitle = 'ra.message.delete_title',
    // TODO: improve translation, and use resource name
    dialogContent: confirmDeleteDialogContent = 'Are you sure you want to delete this item?',
    onConfirmDelete,
  } = deleteOptions || {};

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogConfirm = () => {
    onConfirmDelete && onConfirmDelete();
    setDialogOpen(false);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  const handleDialogClose = () => setDialogOpen(false);

  return (
    <Toolbar {...baseProps}>
      <Grid container justifyContent="space-between">
        <BackButton label="Cancel" />
        <SaveButton disabled={!!disableSave} />
        {showDeleteButton && onConfirmDelete && (
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDialogOpen}>
            Delete
          </Button>
        )}
        {showDeleteButton && onConfirmDelete && (
          <ConfirmDialog
            resource={resource}
            titleTranslateOptions={{ recordRepresentation: record?.id, name: resource }}
            contentTranslateOptions={{ recordRepresentation: record?.id, name: resource }}
            isOpen={dialogOpen}
            title={confirmDeleteDialogTitle}
            content={confirmDeleteDialogContent}
            onConfirm={handleDialogConfirm}
            onClose={handleDialogClose}
            confirm="Delete"
            confirmColor="warning"
            ConfirmIcon={DeleteIcon}
            CancelIcon={CancelIcon}
          />
        )}
        {showDeleteButton && !onConfirmDelete && (
          // TODO: improve style consistency with custom Button + ConfirmDialog above
          <DeleteWithConfirmButton
            variant="contained"
            size="medium"
            confirmTitle={confirmDeleteDialogTitle}
            confirmContent={confirmDeleteDialogContent}
            confirmColor="warning"
          />
        )}
      </Grid>
    </Toolbar>
  );
};

export default FormToolbar;
